import { type QueryClient } from "@tanstack/react-query";

import {
  getGetNotificationUnreadCountQueryKey,
  getListNotificationsQueryKey,
} from "@vega-api/generated/notifications-v1/notifications-v1";
import { type ListNotificationsParams } from "@vega-api/generated/types/listNotificationsParams";
import { type NotificationResponseMeta } from "@vega-api/generated/types/notificationResponseMeta";

import { NOTIFICATIONS_PAGE_SIZE } from "./constants";

/**
 * Filtering for a notification surface.
 * The backend matches notifications whose `meta` contains every key/value pair.
 */
export type NotificationScope = {
  publisher?: string;
  metadata?: Record<string, string>;
};

const scopeFilterParams = (
  scope: NotificationScope,
): ListNotificationsParams => ({
  ...(scope.publisher ? { publisher: scope.publisher } : {}),
  ...(scope.metadata ? { metadata: JSON.stringify(scope.metadata) } : {}),
});

/** Infinite-list query params (first page) for a scope. */
export const getScopedListParams = (
  scope: NotificationScope,
): ListNotificationsParams => ({
  page: 1,
  pageSize: NOTIFICATIONS_PAGE_SIZE,
  ...scopeFilterParams(scope),
});

export const getScopedListQueryKey = (scope: NotificationScope) =>
  getListNotificationsQueryKey(getScopedListParams(scope));

/** Prefix matching every notifications list cache, regardless of filter. */
export const NOTIFICATIONS_LIST_BASE_KEY = getListNotificationsQueryKey();

/** Prefix matching every publisher unread-count cache. */
export const NOTIFICATIONS_UNREAD_COUNT_BASE_KEY =
  getGetNotificationUnreadCountQueryKey();

/**
 * A metadata-scoped unread count has no dedicated endpoint (`unread-count`
 * takes no `metadata`), so it is derived from a 1-row unread list query's
 * `meta.count`. It lives under its OWN key prefix — not the list base — so list
 * cache surgery never touches it.
 */
const METADATA_UNREAD_COUNT_PREFIX = "notifications-metadata-unread-count";

export const METADATA_UNREAD_COUNT_BASE_KEY = [
  METADATA_UNREAD_COUNT_PREFIX,
] as const;

export const getMetadataUnreadCountParams = (
  scope: NotificationScope,
): ListNotificationsParams => ({
  unread: true,
  page: 1,
  pageSize: 1,
  ...scopeFilterParams(scope),
});

export const getMetadataUnreadCountQueryKey = (scope: NotificationScope) =>
  [
    METADATA_UNREAD_COUNT_PREFIX,
    scope.publisher ?? null,
    scope.metadata ?? null,
  ] as const;

/** Publisher-scoped unread count (the `unread-count` endpoint, sidebar badge). */
export const getPublisherUnreadCountQueryKey = (scope: NotificationScope) =>
  getGetNotificationUnreadCountQueryKey(
    scope.publisher ? { publisher: scope.publisher } : undefined,
  );

/** True when `meta` contains every key/value pair in `filter` (mirrors the backend `@>`). */
export const metaContainsFilter = (
  meta: NotificationResponseMeta,
  filter: Record<string, string>,
): boolean =>
  Object.entries(filter).every(([key, value]) => meta[key] === value);

/** metadata filter a list cache was fetched with, if any. */
export const readCacheMetadataFilter = (
  params?: ListNotificationsParams,
): Record<string, string> | undefined => {
  if (!params?.metadata) return undefined;

  try {
    return JSON.parse(params.metadata) as Record<string, string>;
  } catch {
    return undefined;
  }
};

/** Invalidate every notifications query — lists and all unread counts. */
export const invalidateAllNotifications = (queryClient: QueryClient) => {
  queryClient.invalidateQueries({ queryKey: NOTIFICATIONS_LIST_BASE_KEY });
  queryClient.invalidateQueries({
    queryKey: NOTIFICATIONS_UNREAD_COUNT_BASE_KEY,
  });
  queryClient.invalidateQueries({ queryKey: METADATA_UNREAD_COUNT_BASE_KEY });
};
